import { useState, useCallback, useRef } from 'react';
import * as api from '../api/storageApi';
import type { UploadItem } from '../types';

type OverwriteDecision = 'pending' | 'overwrite' | 'overwrite_all' | 'skip' | 'cancel';

const MAX_SIZE_S3 = 5 * 1024 * 1024 * 1024; // 5GB límite de S3
const COMPRESSION_THRESHOLD = 3 * 1024 * 1024 * 1024; // 3GB - comprimir si excede

function validate(file: File): string | null {
  if (file.size > MAX_SIZE_S3) {
    return `El archivo excede el límite de 5 GB de S3. Por favor, comprime el archivo manualmente antes de subirlo.`;
  }
  
  // Ya no validamos el nombre aquí, se sanitiza automáticamente
  return null;
}

function needsCompression(file: File): boolean {
  return file.size > COMPRESSION_THRESHOLD && file.size <= MAX_SIZE_S3;
}

export function useUpload(prefix: string, onSuccess: () => void, onError?: (message: string) => void) {
  const [items, setItems] = useState<UploadItem[]>([]);
  const [overwritePrompt, setOverwritePrompt] = useState<{ fileName: string; resolve: (decision: OverwriteDecision) => void } | null>(null);
  const overwriteAllRef = useRef(false);

  const update = useCallback((id: string, patch: Partial<UploadItem>) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, ...patch } : i));
  }, []);

  const upload = useCallback(async (files: File[]) => {
    if (!files.length) return;

    // Resetear overwriteAll al inicio de cada nueva subida
    overwriteAllRef.current = false;

    const newItems: UploadItem[] = files.map(f => ({
      id:       `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      name:     f.name,
      progress: 0,
      status:   'uploading',
    }));

    setItems(prev => [...prev, ...newItems]);

    let anySuccess = false;

    // Obtener basePath del usuario para asegurar que las subidas respeten restricciones
    const userBasePath = await api.getUserBasePath();
    
    // Cache de archivos existentes por directorio
    const existingFilesCache = new Map<string, Set<string>>();
    
    // Función para obtener archivos existentes en un directorio
    const getExistingFiles = async (dir: string): Promise<Set<string>> => {
      if (existingFilesCache.has(dir)) {
        return existingFilesCache.get(dir)!;
      }
      
      try {
        const result = await api.listFiles(dir, 500);
        const fileNames = new Set(result.files.map(f => f.name));
        existingFilesCache.set(dir, fileNames);
        return fileNames;
      } catch (e) {
        console.warn('Could not check existing files:', e);
        return new Set();
      }
    };
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const item = newItems[i];
      
      // Obtener la ruta relativa si viene de una carpeta
      const relativePath = (file as any).webkitRelativePath || '';
      const pathParts = relativePath ? relativePath.split('/').slice(0, -1) : [];
      
      let dir = prefix.replace(/\/$/, '');
      
      // Agregar la estructura de carpetas si existe
      if (pathParts.length > 0) {
        dir = dir ? `${dir}/${pathParts.join('/')}` : pathParts.join('/');
      }
      
      // Si el usuario tiene basePath y el prefix actual está vacío, usar basePath
      if (userBasePath && !dir) {
        dir = userBasePath;
      }
      // Si el usuario tiene basePath y el prefix no empieza con él, forzar basePath
      else if (userBasePath && !dir.startsWith(userBasePath)) {
        dir = userBasePath + (dir ? '/' + dir : '');
      }

      const err = validate(file);
      if (err) { update(item.id, { status: 'error', error: err }); continue; }
      
      // Advertencia si el archivo necesita compresión
      if (needsCompression(file)) {
        const fileType = file.type || '';
        const isCompressible = fileType.includes('video') || fileType.includes('image') || 
                               fileType.includes('pdf') || fileType.includes('text');
        
        let message = `⚠️ El archivo "${file.name}" tiene ${(file.size / (1024 * 1024 * 1024)).toFixed(2)} GB.\n\n`;
        message += `RECOMENDACIONES PARA AHORRAR ESPACIO:\n\n`;
        
        if (isCompressible) {
          if (fileType.includes('video')) {
            message += `📹 Videos: Usa HandBrake o FFmpeg para reducir calidad/bitrate\n`;
            message += `   - Formato recomendado: H.265/HEVC (50% menos espacio)\n`;
            message += `   - Resolución: 1080p o 720p si es aceptable\n\n`;
          } else if (fileType.includes('image')) {
            message += `🖼️ Imágenes: Comprime con TinyPNG, ImageOptim o Squoosh\n`;
            message += `   - Formato recomendado: WebP o JPEG optimizado\n\n`;
          } else if (fileType.includes('pdf')) {
            message += `📄 PDFs: Usa Adobe Acrobat, Smallpdf o iLovePDF\n`;
            message += `   - Reduce calidad de imágenes internas\n\n`;
          }
        }
        
        message += `📦 Archivos generales: Comprime en .zip, .7z o .rar\n`;
        message += `   - 7-Zip con nivel "Ultra" ahorra más espacio\n\n`;
        message += `⚠️ Límite de S3: 5 GB máximo por archivo\n\n`;
        message += `¿Deseas continuar con la subida de todos modos?`;
        
        const shouldContinue = confirm(message);
        if (!shouldContinue) {
          update(item.id, { status: 'error', error: 'Subida cancelada - Se recomienda comprimir el archivo' });
          continue;
        }
      }
      
      // Verificar si el archivo ya existe en el directorio específico
      const existingFilesInDir = await getExistingFiles(dir);
      if (existingFilesInDir.has(item.name) && !overwriteAllRef.current) {
        const decision = await new Promise<OverwriteDecision>((resolve) => {
          setOverwritePrompt({ fileName: item.name, resolve });
        });
        
        setOverwritePrompt(null);
        
        if (decision === 'skip') {
          update(item.id, { status: 'completed', progress: 100 });
          continue;
        }
        if (decision === 'cancel') {
          update(item.id, { status: 'error', error: 'Cancelado' });
          break;
        }
        if (decision === 'overwrite_all') {
          overwriteAllRef.current = true;
          // Continuar con la subida sin volver a preguntar
        }
        // Si es 'overwrite' o 'overwrite_all', continuar con la subida normal
      }

      try {
        const { uploadURL } = await api.getUploadUrl(item.name, file.type || 'application/octet-stream', dir);
        await api.uploadToUrl(uploadURL, file, pct => update(item.id, { progress: pct }));

        update(item.id, { status: 'completed', progress: 100 });
        anySuccess = true;
      } catch (e) {
        const errorMsg = e instanceof Error ? e.message : 'Error';
        update(item.id, { status: 'error', error: errorMsg });
        if (onError) {
          onError(`Error al subir ${item.name}: ${errorMsg}`);
        }
      }
    }

    if (anySuccess) onSuccess();
  }, [prefix, onSuccess, onError, update]);

  const clear = useCallback(() => setItems([]), []);
  
  const handleOverwriteDecision = useCallback((decision: OverwriteDecision) => {
    if (overwritePrompt) {
      overwritePrompt.resolve(decision);
    }
  }, [overwritePrompt]);

  return { 
    items, 
    upload, 
    clear,
    overwritePrompt: overwritePrompt ? { fileName: overwritePrompt.fileName } : null,
    onOverwrite: () => handleOverwriteDecision('overwrite'),
    onOverwriteAll: () => handleOverwriteDecision('overwrite_all'),
    onSkip: () => handleOverwriteDecision('skip'),
    onCancelOverwrite: () => handleOverwriteDecision('cancel')
  };
}
